import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import SkyOnFireImage from "../../static/images/sky_on_fire.jpg"
import ThemeAmbientOverlay from '../theme-ambient-overlay/ThemeAmbientOverlay';
import { useTheme } from '../../theme/theme-provider';
import './Hero.css'

const GLYPHS = '!@#$%<>[]01ABCDEFabcdef?/\\|=+~^&*';

// Character-scramble hook — decodes to target text then loops
function useScramble(target, speedMs = 50, holdMs = 3000) {
  const [display, setDisplay] = useState(target);
  useEffect(() => {
    let revealed = 0;
    let timer;
    const step = () => {
      if (revealed > target.length) {
        timer = setTimeout(() => { revealed = 0; step(); }, holdMs);
        return;
      }
      setDisplay(
        target.split('').map((ch, i) =>
          i < revealed
            ? ch
            : ch === ' ' ? '\u00A0' : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
        ).join('')
      );
      revealed++;
      timer = setTimeout(step, speedMs);
    };
    step();
    return () => clearTimeout(timer);
  }, [target, speedMs, holdMs]);
  return display;
}

function Hero() {
  const { themeMode } = useTheme();
  const electronicText = 'Electronic Music • Sound Design • Brand Storytelling';
  const electronicPhrase = useScramble('Electronic music', 30, 4500);
  const digitalWord = useScramble('digital', 32, 4300);
  const titleColor = themeMode === 'dark' ? '#ffffff' : '#1c1a18';
  const subtitleColor = themeMode === 'dark' ? 'rgba(255,255,255,0.72)' : '#4d4a46';
  const titleShadow = themeMode === 'dark'
    ? '4px 4px rgba(0,0,0,0.9)'
    : '0 2px 10px rgba(255,255,255,0.45)';

  return (
    <Box id="Hero">

      {/* ── Title bar ─────────────────────────────────── */}
      <Box sx={{ backgroundColor: 'var(--background-color)', position: 'relative', overflow: 'hidden' }}>
        <ThemeAmbientOverlay speed={26} />
        <Typography
          align='center'
          variant='h3'
          sx={{
            color: titleColor,
            fontFamily: 'Light-1',
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem', lg: '3rem' },
            position: 'relative', zIndex: 2,
          }}
        >
          <b style={{ color: titleColor, textShadow: titleShadow }}>Distorise</b>
        </Typography>
        <Typography align='center' variant='body2' sx={{ fontWeight: 700, position: 'relative', zIndex: 2, fontFamily: '"Courier New", monospace', color: subtitleColor, px: 2 }}>
          <b style={{ color: subtitleColor }}>{electronicText}</b>
        </Typography>
      </Box>

      {/* ── Hero image ────────────────────────────────── */}
      <Box sx={{
        backgroundColor: 'var(--background-color)',
        minHeight: { xs: 420, sm: 540, md: 600 },
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <ThemeAmbientOverlay speed={35} />
        <Box sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${SkyOnFireImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          zIndex: 1,
        }} className="HeroImageMotion" />
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: themeMode === 'dark'
            ? 'rgba(2, 3, 3, 0.28)'
            : 'rgba(255, 245, 240, 0.12)',
          backdropFilter: themeMode === 'light' ? 'blur(1px)' : 'none',
          padding: { xs: 2, sm: 3, md: 4 },
          zIndex: 2,
        }}>
          <Typography className="AnimatedText" variant="h4" color="#ffb3b3" align='center' sx={{ fontFamily: '"Courier New", monospace', fontWeight: 700, textShadow: '0 2px 12px rgba(0,0,0,0.85)', maxWidth: 960 }}>
            <span style={{ color: '#ffd0d0' }}>{electronicPhrase}</span> and sound design for stories, brands, and <span style={{ color: '#ffd0d0' }}>{digitalWord}</span> worlds.
          </Typography>
        </Box>
      </Box>

    </Box>
  )
}

export default Hero